import { useCallback, useEffect, useState } from "react";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { PackageTable } from "../components/packages/PackageTable";
import { AddEditPackageDialog } from "../components/packages/AddEditPackageDialog";
import { DeletePackageDialog } from "../components/packages/DeletePackageDialog";
import { PackageStatsDrawer } from "../components/packages/PackageStatsDrawer";
import { ReorderPackagesDialog } from "../components/packages/ReorderPackagesDialog";
import { packageService } from "../services/packageService";
import { useToast } from "../components/ui/ToastContainer";
import { parseApiError } from "../utils/errorHandler";
import type { Package } from "../types/package";

interface PackageStatsResponse {
  package: {
    id: string;
    title: string;
    price: number;
    originalPrice?: number;
    discountPercentage?: number;
  };
  enrollments: {
    total: number;
    byStatus: { _id: string; count: number; totalRevenue: number }[];
  };
  revenue: {
    total: number;
    currency: string;
  };
}

export default function PackagesPage() {
  const { showSuccess, showError } = useToast();
  const [packages, setPackages] = useState<Package[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  const [isAddEditOpen, setIsAddEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isStatsOpen, setIsStatsOpen] = useState(false);
  const [isReorderOpen, setIsReorderOpen] = useState(false);
  const [statsData, setStatsData] = useState<PackageStatsResponse | null>(null);
  const [isStatsLoading, setIsStatsLoading] = useState(false);

  const fetchPackages = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await packageService.getAdminPackages({
        limit: 100,
        sortBy: "createdAt",
        order: "desc",
      });
      setPackages(response.data?.packages || response.packages || []);
    } catch (err) {
      const apiError = parseApiError(err);
      showError(apiError.message, "Failed to load packages");
    } finally {
      setIsLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  const handleAddPackage = () => {
    setSelectedPackage(null);
    setIsAddEditOpen(true);
  };

  const handleEditPackage = (pkg: Package) => {
    setSelectedPackage(pkg);
    setIsAddEditOpen(true);
  };

  const handleDeletePackage = (pkg: Package) => {
    setSelectedPackage(pkg);
    setIsDeleteOpen(true);
  };

  const handleTogglePublish = async (pkg: Package) => {
    try {
      await packageService.updatePackage(pkg._id, {
        isPublished: !pkg.isPublished,
      });
      showSuccess(
        `Package ${pkg.isPublished ? "unpublished" : "published"} successfully`,
        "Success"
      );
      fetchPackages();
    } catch (err) {
      const apiError = parseApiError(err);
      showError(apiError.message, "Failed to update publish status");
    }
  };

  const handleToggleActive = async (pkg: Package) => {
    try {
      await packageService.updatePackage(pkg._id, {
        isActive: !pkg.isActive,
        ...(pkg.isActive ? { isPublished: false } : {}),
      });
      showSuccess(
        `Package ${pkg.isActive ? "deactivated" : "activated"} successfully`,
        "Success"
      );
      fetchPackages();
    } catch (err) {
      const apiError = parseApiError(err);
      showError(apiError.message, "Failed to update package status");
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedPackage) return;
    try {
      await packageService.deletePackage(selectedPackage._id);
      showSuccess("Package deactivated successfully", "Success");
      fetchPackages();
    } catch (err) {
      const apiError = parseApiError(err);
      showError(apiError.message, "Failed to delete package");
      throw apiError;
    }
  };

  const handleViewStats = async (pkg: Package) => {
    setSelectedPackage(pkg);
    setIsStatsOpen(true);
    setIsStatsLoading(true);
    try {
      const response = await packageService.getPackageStats(pkg._id);
      setStatsData(response.data?.stats || response.stats || null);
    } catch (err) {
      const apiError = parseApiError(err);
      showError(apiError.message, "Failed to load package stats");
      setIsStatsOpen(false);
    } finally {
      setIsStatsLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <PackageTable
        packages={packages}
        isLoading={isLoading}
        onAddClick={handleAddPackage}
        onEditClick={handleEditPackage}
        onDeleteClick={handleDeletePackage}
        onTogglePublish={handleTogglePublish}
        onToggleActive={handleToggleActive}
        onViewStats={handleViewStats}
        onReorderClick={() => setIsReorderOpen(true)}
      />

      <AddEditPackageDialog
        isOpen={isAddEditOpen}
        onClose={() => setIsAddEditOpen(false)}
        onSuccess={fetchPackages}
        pkg={selectedPackage}
      />

      <DeletePackageDialog
        isOpen={isDeleteOpen}
        pkg={selectedPackage}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={handleDeleteConfirm}
      />

      <PackageStatsDrawer
        isOpen={isStatsOpen}
        onClose={() => {
          setIsStatsOpen(false);
          setStatsData(null);
        }}
        pkg={selectedPackage}
        stats={statsData}
        isLoading={isStatsLoading}
      />

      <ReorderPackagesDialog
        isOpen={isReorderOpen}
        onClose={() => setIsReorderOpen(false)}
        onSuccess={fetchPackages}
      />
    </DashboardLayout>
  );
}
